/**
 * Картка запису — стабільна адреса `/r/<сутність>/<id>` (UX-03, REC-01): картка платформи на `/meta`
 * (`platform/CardPage.tsx`, E6a-W10). Без входу вона веде на вхід з поверненням сюди (демо S4 без
 * входу прибрано, тікет 8e4d4110).
 *
 * Картка — лінивий чанк: `preload` компонента вантажить його заздалегідь (попереднє завантаження,
 * `app/preload.ts`); після 300 мс очікування — скелетон (states.md, розд. 2).
 *
 * `loader` (аудит якості № 1) — за зразком списку (routes/platform.ts): чанк картки й метадані
 * паралельно, далі запис і макети (`preloadCard` у чанку картки). Роутер тримає попередній екран до
 * готовності картки, після 300 мс — скелетон з назвою запису з рядка списку (`CardPending`).
 * Вкладка й пошук «Усіх полів» — в адресі (`tab`, `q`), не в `loaderDeps`: їх зміна (`replace`) —
 * той самий збіг маршруту, і роутер `loader` не перезапускає (усе вже в кеші).
 *
 * `cause` у router-core 1.171 — за id маршруту, а не за збігом: `'enter'` — прихід на картку з
 * іншого екрана, `'stay'` — картка лишається (інший запис `J`/`K`, «Назад» між картками). Новий
 * збіг (інший запис) `loader` має завжди; щойно відвіданий із кешу роутера перечитується фоном.
 * На `'stay'` лінію «Стрічки» `loader` чекає з чипом картки на екрані, на `'enter'` — «Усе»: той
 * самий ключ, що картка покаже, тож запит не дублюється, а `J` приєднується до свого читання
 * наперед (рев'ю #885, коло 3, S1; card/feed-wait.ts).
 * Наведення на рядок списку вантажить лише код (`loadRouteCode`, list-source.tsx), не цей `loader`.
 */
import { createRoute } from '@tanstack/react-router';
import { type ComponentType, createElement } from 'react';
import { CardPending } from '../../platform/card/CardPending.tsx';
import { queryParamOf, tabParamOf } from '../../platform/card/card-params.ts';
import { platformMetaQuery } from '../../platform/meta.ts';
import { actParamOf, fullParamOf, openParamOf } from '../../platform/open-param.ts';
import { RoutePending } from '../../ui/RoutePending.tsx';
import { SkeletonCard } from '../../ui/Skeleton.tsx';
import { useRoutePending } from '../route-pending.ts';
import { RouteError, rootRoute } from './root.tsx';

/** Що відкрити в картці одразу: `log` — запис виконаної активності (`L`), `call` — дзвінок. */
export type ResultMode = 'call' | 'log';
export interface RecordSearch {
  result?: ResultMode;
  /**
   * Дзвінок, результат якого записуємо в цій картці (Г2-W1; `features/call/CallArea.tsx`): рядок
   * дзвінка й блок результату над вкладками. Лише UUID, решта значень відкидається.
   */
  call?: string;
  /**
   * Запис, відкритий фінансовою панеллю поверх картки (Б5-W1; layout.md, розд. 7, правило 9):
   * `<сутність>:<UUID>`. Решта значень відкидається.
   */
  open?: string;
  /** Крок панелі, з яким відкрито картку: `pay` — новий платіж (Б5-W1). */
  act?: 'pay';
  /** Повна картка запису з `open: 'panel'` («Відкрити повністю»), а не панель на господарі. */
  full?: '1';
  /**
   * Вкладка центру (аудит якості № 2, 3): «Назад», `J` / `K` і посилання відкривають ту саму.
   * «Стрічка» — без параметра; вкладки, якої в картці немає, картка не покаже.
   */
  tab?: string;
  /** Пошук «Усіх полів» (REC-03). */
  q?: string;
  /** Id поглиненого запису, зі старої адреси якого прийшли (REC-01, DAT-08): банер на основному. */
  merged?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Лінивий компонент з повтором: збій завантаження чанка — помилка маршруту (стан помилки з «Ще раз»),
 * а повторне завантаження маршруту (`preload`) пробує ще раз. `React.lazy` запам'ятав би відмову.
 */
function lazyComponent<P extends object>(load: () => Promise<ComponentType<P>>) {
  let component: ComponentType<P> | undefined;
  let pending: Promise<void> | undefined;
  let failure: unknown;
  const preload = () => {
    if (component) return Promise.resolve();
    if (!pending) {
      failure = undefined;
      pending = load().then(
        (c) => {
          component = c;
        },
        (error: unknown) => {
          pending = undefined;
          failure = error;
        },
      );
    }
    return pending;
  };
  function Lazy(props: P) {
    if (failure) throw failure;
    if (!component) throw preload();
    return createElement(component, props);
  }
  Lazy.preload = preload;
  return Lazy;
}

const cardChunk = () => import('../../platform/CardPage.tsx');
const PlatformCard = lazyComponent(() => cardChunk().then((m) => m.CardPage));
const PlatformCreate = lazyComponent(() =>
  import('../../platform/create/CreatePage.tsx').then((m) => m.CreatePage),
);
function RecordRoute() {
  return createElement(PlatformCard, {});
}
RecordRoute.preload = () => PlatformCard.preload();

export const recordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/r/$entity/$recordId',
  validateSearch: (search: Record<string, unknown>): RecordSearch => ({
    ...(search.result === 'call' || search.result === 'log' ? { result: search.result } : {}),
    ...(typeof search.call === 'string' && UUID.test(search.call) ? { call: search.call } : {}),
    ...(openParamOf(search.open) ? { open: search.open as string } : {}),
    ...(actParamOf(search.act) ? { act: 'pay' as const } : {}),
    ...(fullParamOf(search.full) ? { full: '1' as const } : {}),
    ...(tabParamOf(search.tab) ? { tab: search.tab as string } : {}),
    ...(queryParamOf(search.q) ? { q: queryParamOf(search.q) } : {}),
    ...(typeof search.merged === 'string' && UUID.test(search.merged)
      ? { merged: search.merged }
      : {}),
  }),
  loader: async ({ context, params, preload, cause }) => {
    const client = context.queryClient;
    // Попереднє завантаження маршруту (`preloadRoute`) — лише код картки: дані наперед тягне лише
    // `J` (UX-33, speed.md), інакше кожен намір — читання запису для SEC-072 / SEC-066.
    if (preload) {
      await cardChunk().catch(() => null);
      return;
    }
    // Збій чанка тут не кидаємо: його покаже компонент маршруту (помилка з «Ще раз», повтор).
    const [card] = await Promise.all([
      cardChunk().catch(() => null),
      client.ensureQueryData(platformMetaQuery()).catch(() => undefined),
    ]);
    await card?.preloadCard(client, params.entity, params.recordId, { stay: cause === 'stay' });
  },
  component: RecordRoute,
  // Скелетон трьох колонок — у першому екрані, поки вантажаться чанк і дані картки.
  pendingComponent: CardPending,
  // Збій завантаження чанка картки. Стани даних картка платформи показує сама.
  errorComponent: RouteError,
});

/**
 * Повна форма створення (DAT-02) — `/r/<сутність>/new`: статичний сегмент важливіший за `$recordId`,
 * а id запису — UUID, тож «new» ним не буває. Лише платформа: без входу форма веде на вхід з
 * поверненням сюди. Лінивий чанк форми з повтором після збою, як у картки.
 */
function CreateRoute() {
  return createElement(PlatformCreate, {});
}
CreateRoute.preload = () => PlatformCreate.preload();

/** Очікування форми створення: скелетон картки з фазами маршруту (`RoutePending`). */
function CreatePending() {
  return createElement(RoutePending, {
    state: useRoutePending(),
    skeleton: createElement(SkeletonCard),
  });
}

export const createRecordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/r/$entity/new',
  component: CreateRoute,
  pendingComponent: CreatePending,
  errorComponent: RouteError,
});
